import type { SharedSelection } from '@heroui/react';
import {
  Button,
  ButtonGroup,
  Select,
  SelectItem,
  Tooltip,
} from '@heroui/react';
import { useMemo, type ReactElement } from 'react';
import { Icon } from '@iconify/react';

function Container({
  rounded = true,
  className,
  children,
}: {
  rounded?: boolean;
  className?: string;
  children?: ReactElement | ReactElement[] | string;
}) {
  return (
    <div
      className={`${className || ''} flex flex-row items-center ${rounded ? 'rounded-medium' : ''} bg-default-100 px-4 py-2 text-sm`}
    >
      {children}
    </div>
  );
}

export function AbsolutePagingInfo({
  page,
  itemsPerPage,
  count,
  className,
}: {
  page: number;
  itemsPerPage: number;
  count: number;
  className?: string;
}) {
  const info = getInfo({ page, itemsPerPage, count });

  return <Container className={className}>{info}</Container>;
}

function getInfo({
  page,
  itemsPerPage,
  count,
}: {
  page: number;
  itemsPerPage: number;
  count: number;
}) {
  const offsetStart = page < 1 ? 0 : (page - 1) * itemsPerPage + 1;
  const offsetEnd = Math.min(page * itemsPerPage, count);
  const startString = Number(offsetStart).toLocaleString();
  const endString = Number(offsetEnd).toLocaleString();
  const countString = Number(count).toLocaleString();
  const info =
    page === 0 || count === 0
      ? ''
      : `${startString} - ${endString} of ${countString}`;

  return info;
}

export function SequentialPagination({
  page,
  itemsPerPage,
  allowItemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  count,
  className,
}: {
  page: number;
  itemsPerPage: number;
  allowItemsPerPage?: number[];
  onPageChange: (page: number) => void;
  onItemsPerPageChange?: (value: number) => void;
  count: number;
  className?: string;
}) {
  const atStart = page === 1;
  const atEnd = page * itemsPerPage >= count;
  const prevPage = () => {
    if (atStart) {
      return;
    }

    onPageChange(page - 1);
  };

  const nextPage = () => {
    if (atEnd) {
      return;
    }

    onPageChange(page + 1);
  };

  const info = getInfo({ page, itemsPerPage, count });
  const itemsPerPageList = useMemo(() => {
    if (!allowItemsPerPage) {
      return undefined;
    }

    return allowItemsPerPage.map((item) => ({
      value: item,
      text: String(item),
    }));
  }, [allowItemsPerPage]);
  const itemsPerChangeHandler = (keys: SharedSelection) => {
    if (!onItemsPerPageChange) {
      return;
    }

    const value = parseInt(keys.currentKey || '0', 10);
    onItemsPerPageChange(value);
  };

  return (
    <div
      className={`${className} flex flex-row items-center rounded-medium bg-default-100 pl-4 text-sm`}
    >
      <span className='mr-2 text-nowrap'>{info}</span>
      <ButtonGroup className='flex-grow'>
        <Button
          isIconOnly
          className='w-10 bg-transparent disabled:text-gray-400'
          disabled={atStart}
          onPress={prevPage}
        >
          <Icon icon='mdi:chevron-left' width={16} height={16} />
        </Button>
        <Button
          isIconOnly
          className='w-10 bg-transparent disabled:text-gray-400'
          disabled={atEnd}
          onPress={nextPage}
        >
          <Icon icon='mdi:chevron-right' width={16} height={16} />
        </Button>
      </ButtonGroup>
      {itemsPerPageList && (
        <>
          <Tooltip content='Items Per Page' offset={48}>
            <Select
              className='w-20'
              variant='flat'
              items={itemsPerPageList}
              selectedKeys={[String(itemsPerPage)]}
              onSelectionChange={itemsPerChangeHandler}
            >
              {itemsPerPageList.map((item) => (
                <SelectItem key={item.value}>{item.text}</SelectItem>
              ))}
            </Select>
          </Tooltip>
        </>
      )}
    </div>
  );
}
